import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, Target, Heart, Zap, Scale, Activity, Ruler, Shirt, Calendar as CalendarIcon, Dumbbell, Bike, Users, Waves, Play, Store, MapPin, ShoppingBag, Users2, Shield, Bot, MessageSquare, MessageCircle, CalendarCheck, CalendarDays, Utensils, Sparkles, SparklesIcon, LogOut, Trophy } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { listPricingTiers } from "@/integrations/backend/pricing";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { ServiceCard } from "@/components/ui/service-card";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import DobPicker from "@/components/ui/dob-picker";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { format, isValid as isValidDate, parse, parseISO } from "date-fns";
import { getOnboarding, saveOnboarding, type OnboardingData } from "@/integrations/backend/onboarding";
import { queryKeys } from "@/lib/query-keys";
import { createCheckoutSession } from "@/integrations/backend/stripe";
import { parseYYYYMMDDToLocalDate } from "@/lib/date-only";

// Define a more strict type for the form data to avoid type errors
interface FormData {
  motivation: string[];
  goals: string[];
  activityLevel: string;
  workoutFrequency: string;
  workoutDuration: string;
  location: string[];
  exerciseTypes: string[];
  sportsEnjoyed: string;
  healthConditions: string;
  injuries: string;
  fitnessLevel: string;
  successMetrics: string[];
  gymPersona: string[];
  resultTimeframe: string;
  specificGoals: Record<string, string>;
  currentMetrics: Record<string, string>;
  additionalServices: string[];
  selectedPlan: string;
  billingCycle: "monthly" | "annual";
  dob?: string;
  heightFeet?: string;
  heightInches?: string;
  bodyWeight?: string;
  gender?: string;
  heightCm?: string;
  heightUnit?: "imperial" | "metric";
  weightUnit?: "lb" | "kg";
  trainingForEvent?: string;
  eventDetails?: string;
  eventDate?: string;
}

const TOTAL_STEPS = 9;

const createInitialFormData = (): FormData => ({
  motivation: [],
  goals: [],
  activityLevel: "",
  workoutFrequency: "",
  workoutDuration: "",
  location: [],
  exerciseTypes: [],
  sportsEnjoyed: "",
  healthConditions: "",
  injuries: "",
  fitnessLevel: "",
  successMetrics: [],
  gymPersona: [],
  resultTimeframe: "",
  specificGoals: {},
  currentMetrics: {},
  additionalServices: [],
  selectedPlan: "",
  billingCycle: "monthly",
  dob: "",
  heightFeet: "",
  heightInches: "",
  bodyWeight: "",
  gender: "",
  heightCm: "",
  heightUnit: "imperial",
  weightUnit: "lb",
  trainingForEvent: "",
  eventDetails: "",
  eventDate: ""
});

const locationCodeToDisplay = (code: string): string => {
  const mapping: Record<string, string> = {
    home: "At Home",
    gym: "Commercial Gym",
    outdoor: "Outdoor / Park",
    mixed: "Mixed",
  };
  return mapping[code] || code;
};

const buildFormDataFromOnboarding = (onboardingData: OnboardingData): FormData => ({
  ...createInitialFormData(),
  gender: onboardingData.gender || "",
  dob: onboardingData.dob || "",
  heightFeet: onboardingData.heightFeet?.toString() || "",
  heightInches: onboardingData.heightInches?.toString() || "",
  heightCm: onboardingData.heightCm?.toString() || "",
  heightUnit: (onboardingData.heightUnit as "imperial" | "metric") || "imperial",
  bodyWeight: onboardingData.bodyWeight?.toString() || "",
  weightUnit: (onboardingData.weightUnit as "lb" | "kg") || "lb",
  motivation: onboardingData.motivation || [],
  goals: onboardingData.goals || [],
  activityLevel: onboardingData.activityLevel || "",
  workoutFrequency: onboardingData.workoutFrequency || "",
  workoutDuration: onboardingData.workoutDuration || "",
  location: (onboardingData.location || []).map(locationCodeToDisplay),
  exerciseTypes: onboardingData.exerciseTypes || [],
  sportsEnjoyed: onboardingData.sportsEnjoyed || "",
  healthConditions: onboardingData.healthConditions || "",
  injuries: onboardingData.injuries || "",
  fitnessLevel: onboardingData.fitnessLevel || "",
  successMetrics: onboardingData.successMetrics || [],
  gymPersona: onboardingData.gymPersona || [],
  resultTimeframe: onboardingData.resultTimeframe || "",
  specificGoals: onboardingData.specificGoals || {},
  currentMetrics: onboardingData.currentMetrics || {},
  additionalServices: onboardingData.additionalServices || [],
  selectedPlan: onboardingData.selectedPlan || "",
  billingCycle: (onboardingData.billingCycle as "monthly" | "annual") || "monthly",
  trainingForEvent: onboardingData.trainingForEvent || "",
  eventDetails: onboardingData.eventDetails || "",
  eventDate: onboardingData.eventDate || "",
});

const hasValidPersonalDetails = (data: FormData): boolean => {
  const parsedIso =
    data.dob && /^\d{4}-\d{2}-\d{2}$/.test(data.dob)
      ? parseYYYYMMDDToLocalDate(data.dob)
      : null;
  const parsedUs = data.dob ? parse(data.dob, "MM/dd/yyyy", new Date()) : null;
  const dobDate = parsedIso && isValidDate(parsedIso) ? parsedIso : (parsedUs && isValidDate(parsedUs) ? parsedUs : null);
  const dobOk = !!dobDate && (() => {
    const now = new Date();
    const age = now.getFullYear() - dobDate.getFullYear() - ((now.getMonth() < dobDate.getMonth() || (now.getMonth() === dobDate.getMonth() && now.getDate() < dobDate.getDate())) ? 1 : 0);
    return age >= 13 && age <= 100;
  })();
  const feet = Number(data.heightFeet);
  const inches = Number(data.heightInches);
  const heightCm = Number(data.heightCm);
  const weight = Number(data.bodyWeight);
  const heightOk = data.heightUnit === "metric"
    ? Number.isFinite(heightCm) && heightCm > 0
    : Number.isFinite(feet) && feet >= 0 && Number.isFinite(inches) && inches >= 0 && inches <= 11 && (feet > 0 || inches > 0);
  const weightOk = Number.isFinite(weight) && weight > 0;
  const genderOk = !!data.gender;
  return dobOk && heightOk && weightOk && genderOk;
};

const getFirstIncompleteStep = (data: FormData): number => {
  if (!hasValidPersonalDetails(data)) return 1;
  if (data.motivation.length === 0) return 2;
  if (!data.activityLevel) return 3;
  if (!data.workoutFrequency || !data.workoutDuration) return 4;
  if (data.exerciseTypes.length === 0 || (data.exerciseTypes.includes("sports") && !data.sportsEnjoyed.trim())) return 5;
  if (data.location.length === 0) return 6;
  if (!data.resultTimeframe) return 7;
  if (data.trainingForEvent === "yes" && !(data.eventDetails?.trim() && data.eventDate)) return 8;
  return 9;
};

const ClientOnboarding = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(createInitialFormData);
  const {
    user,
    profile,
    signOut
  } = useAuth();
  const queryClient = useQueryClient();
  const totalSteps = TOTAL_STEPS; // Total steps (added Activity Level step)
  const progress = (currentStep - 1) / totalSteps * 100;
  const isMobile = useIsMobile();

  // Map current step to view step indexes
  // Mapping: 1->1, 2->2, 3->3, 4->4, 5->5, 6->6, 7->7, 8->8, 9->9
  const viewStep = currentStep;

  // Pretty progress metadata for sticky top indicator
  const steps = ["Welcome", "Motivation", "Activity", "Frequency", "Types", "Location", "Timeline", "Event", "Plan"];
  const activeIndex = Math.max(0, Math.min(steps.length - 1, currentStep - 1));

  // Store the beforeunload handler in a ref so we can remove it later
  const beforeUnloadHandlerRef = useRef<((e: BeforeUnloadEvent) => void) | null>(null);

  // Prevent navigation away from the page accidentally
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    beforeUnloadHandlerRef.current = handleBeforeUnload;
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      if (beforeUnloadHandlerRef.current) {
        window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
      }
    };
  }, []);

  const onboardingMeKey = queryKeys.onboarding.me;
  const onboardingQuery = useQuery({
    queryKey: onboardingMeKey,
    queryFn: getOnboarding,
    enabled: Boolean(user),
    staleTime: 0,
  });

  const onboardingHydratedFromServerRef = useRef(false);
  useEffect(() => {
    onboardingHydratedFromServerRef.current = false;
    setFormData(createInitialFormData());
    setCurrentStep(1);
  }, [user?.id]);

  // Hydrate form from GET /onboarding/me once per user session (avoid overwriting in-progress edits on refetch)
  useEffect(() => {
    if (!user || !onboardingQuery.isSuccess || onboardingHydratedFromServerRef.current) return;
    onboardingHydratedFromServerRef.current = true;

    try {
      const onboardingData = onboardingQuery.data;

      if (onboardingData.id) {
        const hydratedFormData = buildFormDataFromOnboarding(onboardingData);
        setFormData(hydratedFormData);
        setCurrentStep(getFirstIncompleteStep(hydratedFormData));
      } else {
        setFormData(createInitialFormData());
        setCurrentStep(1);
      }
    } catch (error) {
      console.error("Failed to apply onboarding data:", error);
    }
  }, [user, onboardingQuery.isSuccess, onboardingQuery.data]);

  // Scroll to top when step changes
  useEffect(() => {
    scrollToTop();
  }, [currentStep]);
  const motivations = [{
    id: "lose-weight",
    label: "Lose Weight",
    icon: "🎯"
  }, {
    id: "build-muscle",
    label: "Build Muscle",
    icon: "💪"
  }, {
    id: "increase-strength",
    label: "Increase Strength",
    icon: "🏋️"
  }, {
    id: "improve-health",
    label: "Improve Health",
    icon: "❤️"
  }, {
    id: "boost-energy",
    label: "Boost Energy",
    icon: "⚡"
  }, {
    id: "reduce-stress",
    label: "Reduce Stress",
    icon: "🧘"
  }, {
    id: "sport-performance",
    label: "Sport Performance",
    icon: "🏃"
  }];
  const equipment = ["Dumbbells", "Resistance Bands", "Yoga Mat", "Pull-up Bar", "Kettlebells", "Barbell", "Bench", "Cardio Machine", "None"];
  const successMetrics = [{
    id: "body-weight",
    label: "Body Weight",
    icon: "⚖️",
    color: "from-blue-100 to-blue-200",
    hoverColor: "hover:from-blue-200 hover:to-blue-300"
  }, {
    id: "body-fat",
    label: "Body Fat %",
    icon: "📉",
    color: "from-green-100 to-green-200",
    hoverColor: "hover:from-green-200 hover:to-green-300"
  }, {
    id: "bmi",
    label: "BMI",
    icon: "📊",
    color: "from-yellow-100 to-yellow-200",
    hoverColor: "hover:from-yellow-200 hover:to-yellow-300"
  }, {
    id: "visceral-fat",
    label: "Visceral Fat",
    icon: "🔍",
    color: "from-orange-100 to-orange-200",
    hoverColor: "hover:from-orange-200 hover:to-orange-300"
  }, {
    id: "jeans-size",
    label: "Jeans Size",
    icon: "👖",
    color: "from-indigo-100 to-indigo-200",
    hoverColor: "hover:from-indigo-200 hover:to-indigo-300"
  }, {
    id: "tshirt-size",
    label: "T-Shirt Size",
    icon: "👕",
    color: "from-purple-100 to-purple-200",
    hoverColor: "hover:from-purple-200 hover:to-purple-300"
  }, {
    id: "energy-level",
    label: "Energy Level",
    icon: "⚡",
    color: "from-red-100 to-red-200",
    hoverColor: "hover:from-red-200 hover:to-red-300"
  }, {
    id: "strength",
    label: "Strength",
    icon: "💪",
    color: "from-emerald-100 to-emerald-200",
    hoverColor: "hover:from-emerald-200 hover:to-emerald-300"
  }];
  const activityLevelOptions = [{
    value: "sedentary",
    label: "Sedentary",
    description: "I do little to no structured physical activity or exercise"
  }, {
    value: "lightly-active",
    label: "Lightly Active",
    description: "I walk or move casually during the day and may do light activity (e.g., Yoga, stretching, or short walks) 2-3 times per week"
  }, {
    value: "moderately-active-cardio",
    label: "Moderately Active (Cardio Focused)",
    description: "I engage in cardio-based activities (e.g., running, biking, group fitness classes) 2-3x per week with minimal or no strength training"
  }, {
    value: "moderately-active-strength",
    label: "Moderately Active (Strength-focused)",
    description: "I perform strength training (e.g., weights, bodyweight exercises) 2-3 times per week but little to no cardio"
  }, {
    value: "very-active",
    label: "Very Active",
    description: "I combine both cardio and strength focused training 4-5x per week consistently"
  }, {
    value: "fully-active",
    label: "Fully Active / Athletic",
    description: "I train almost daily with a well-rounded routine that includes strength, cardio, mobility and recovery practises"
  }];
  const workoutFrequencyOptions = [{
    value: "2-times",
    label: "2 times per week",
    desc: "Perfect for beginners",
    emoji: "🎯",
    color: "from-emerald-100 to-emerald-200",
    hoverColor: "hover:from-emerald-200 hover:to-emerald-300",
    activeColor: "from-emerald-300 to-emerald-400",
    trophy: {
      name: "Fitness Explorer",
      points: 50,
      description: "Earn this trophy for completing 2 workouts weekly"
    }
  }, {
    value: "3-times",
    label: "3 times per week",
    desc: "Most popular choice",
    emoji: "💪",
    color: "from-blue-100 to-blue-200",
    hoverColor: "hover:from-blue-200 hover:to-blue-300",
    activeColor: "from-blue-300 to-blue-400",
    trophy: {
      name: "Dedicated Athlete",
      points: 100,
      description: "Earn this trophy for completing 3 workouts weekly"
    }
  }, {
    value: "4-times",
    label: "4 times per week",
    desc: "For dedicated fitness enthusiasts",
    emoji: "🔥",
    color: "from-orange-100 to-orange-200",
    hoverColor: "hover:from-orange-200 hover:to-orange-300",
    activeColor: "from-orange-300 to-orange-400",
    trophy: {
      name: "Fitness Champion",
      points: 150,
      description: "Earn this trophy for completing 4 workouts weekly"
    }
  }, {
    value: "5-plus",
    label: "5+ times per week",
    desc: "For serious athletes",
    emoji: "⚡",
    color: "from-red-100 to-red-200",
    hoverColor: "hover:from-red-200 hover:to-red-300",
    activeColor: "from-red-300 to-red-400",
    trophy: {
      name: "Elite Performer",
      points: 200,
      description: "Earn this trophy for completing 5+ workouts weekly"
    }
  }];

  const workoutDurationOptions = [
    { value: "30-min", label: "30 min" },
    { value: "45-min", label: "45 min" },
    { value: "60-plus-min", label: "60+ min" }
  ];
  const exerciseTypeOptions = [{
    id: "strength-training",
    label: "Strength Training",
    description: "Build muscle and increase strength",
    icon: "🏋️",
    color: "from-blue-100 to-blue-200",
    hoverColor: "hover:from-blue-200 hover:to-blue-300"
  }, {
    id: "cardio",
    label: "Cardio",
    description: "Improve heart health and endurance",
    icon: "❤️",
    color: "from-red-100 to-red-200",
    hoverColor: "hover:from-red-200 hover:to-red-300"
  }, {
    id: "running",
    label: "Running",
    description: "Outdoor or treadmill running",
    icon: "🏃",
    color: "from-green-100 to-green-200",
    hoverColor: "hover:from-green-200 hover:to-green-300"
  }, {
    id: "cycling",
    label: "Cycling",
    description: "Indoor or outdoor cycling",
    icon: "🚴",
    color: "from-amber-100 to-amber-200",
    hoverColor: "hover:from-amber-200 hover:to-amber-300"
  }, {
    id: "swimming",
    label: "Swimming",
    description: "Low-impact full-body workout",
    icon: "🏊",
    color: "from-cyan-100 to-cyan-200",
    hoverColor: "hover:from-cyan-200 hover:to-cyan-300"
  }, {
    id: "group-classes",
    label: "Group Classes",
    description: "Motivating workout with others",
    icon: "👯",
    color: "from-purple-100 to-purple-200",
    hoverColor: "hover:from-purple-200 hover:to-purple-300"
  }, {
    id: "sports",
    label: "Sports",
    description: "Basketball, soccer, tennis, etc.",
    icon: "🏅",
    color: "from-orange-100 to-orange-200",
    hoverColor: "hover:from-orange-200 hover:to-orange-300"
  }, {
    id: "hiit",
    label: "HIIT",
    description: "High-intensity interval training",
    icon: "⚡",
    color: "from-pink-100 to-pink-200",
    hoverColor: "hover:from-pink-200 hover:to-pink-300"
  }];
  const gymPersonaOptions = [{
    id: "form-focused",
    label: "Form Master",
    description: "Perfect technique is your superpower. You'd rather do 5 perfect reps than 20 sloppy ones.",
    icon: "🧘‍♂️",
    color: "from-blue-100 to-indigo-200",
    hoverColor: "hover:from-blue-200 hover:to-indigo-300",
    traits: ["Patient", "Precise", "Detail-oriented"]
  }, {
    id: "strength-seeker",
    label: "Strength Seeker",
    description: "You're all about those strength gains and pushing your limits with heavy weights.",
    icon: "💪",
    color: "from-red-100 to-orange-200",
    hoverColor: "hover:from-red-200 hover:to-orange-300",
    traits: ["Determined", "Powerful", "Goal-driven"]
  }, {
    id: "endurance-enthusiast",
    label: "Endurance Enthusiast",
    description: "More reps, longer sessions - you thrive on testing your stamina and endurance.",
    icon: "🏃",
    color: "from-green-100 to-emerald-200",
    hoverColor: "hover:from-green-200 hover:to-emerald-300",
    traits: ["Persistent", "Steady", "Focused"]
  }, {
    id: "social-butterfly",
    label: "Social Butterfly",
    description: "The gym is your social hub. You love making friends and working out together.",
    icon: "🦋",
    color: "from-purple-100 to-pink-200",
    hoverColor: "hover:from-purple-200 hover:to-pink-300",
    traits: ["Friendly", "Motivating", "Team player"]
  }, {
    id: "data-tracker",
    label: "Data Tracker",
    description: "You log every workout, track all your stats, and love seeing your progress in numbers.",
    icon: "📊",
    color: "from-cyan-100 to-sky-200",
    hoverColor: "hover:from-cyan-200 hover:to-sky-300",
    traits: ["Analytical", "Organized", "Progress-focused"]
  }, {
    id: "wellness-balancer",
    label: "Wellness Balancer",
    description: "You see the gym as part of a holistic lifestyle with nutrition, sleep, and mindfulness.",
    icon: "⚖️",
    color: "from-amber-100 to-yellow-200",
    hoverColor: "hover:from-amber-200 hover:to-yellow-300",
    traits: ["Mindful", "Balanced", "Holistic"]
  }];
  const timeframeOptions = [{
    value: "1-month",
    label: "1 Month (Quick Results)",
    description: "Intensive focus for fast changes"
  }, {
    value: "3-months",
    label: "3 Months (Balanced Approach)",
    description: "Most popular timeframe for sustainable results"
  }, {
    value: "6-months",
    label: "6 Months (Steady Progress)",
    description: "Gradual, lasting transformation"
  }, {
    value: "12-months",
    label: "12 Months (Long-term Commitment)",
    description: "Complete lifestyle transformation"
  }];
  const additionalServiceOptions = [{
    id: "find-gym",
    title: "Find a Gym",
    description: "Discover perfect gyms near you with our personalized matching service",
    icon: "🏋️",
    color: "from-blue-50 to-indigo-100",
    hoverColor: "hover:from-blue-100 hover:to-indigo-200",
    benefits: ["Personalized gym recommendations", "Exclusive membership discounts", "Virtual tours before joining"],
    lucideIcon: MapPin
  }, {
    id: "workout-gear",
    title: "Workout Apparel",
    description: "High-performance workout clothes at exclusive member prices",
    icon: "👕",
    color: "from-emerald-50 to-teal-100",
    hoverColor: "hover:from-emerald-100 hover:to-teal-200",
    benefits: ["Member-only discounts", "Curated collections for your style", "Free shipping on orders"],
    lucideIcon: ShoppingBag
  }, {
    id: "equipment",
    title: "Home Equipment",
    description: "Build your home gym with quality equipment at special rates",
    icon: "💪",
    color: "from-amber-50 to-orange-100",
    hoverColor: "hover:from-amber-100 hover:to-orange-200",
    benefits: ["Expert equipment recommendations", "Exclusive pricing for members", "Free delivery & setup"],
    lucideIcon: Dumbbell
  }, {
    id: "gym-buddies",
    title: "Find Gym Buddies",
    description: "Connect with workout partners who have similar goals and schedules",
    icon: "👥",
    color: "from-purple-50 to-fuchsia-100",
    hoverColor: "hover:from-purple-100 hover:to-fuchsia-200",
    benefits: ["Match by fitness goals & level", "Scheduled workout sessions", "Build your fitness community"],
    lucideIcon: Users2
  }];

  // Add subscription plans data
  type Billing = "monthly" | "annual";
  type Tier = {
    id: string;
    name: string;
    blurb: string;
    features: string[];
    price_monthly: number;
    price_annual: number;
    highlight: boolean;
    sort_order: number;
  };
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const rows = await listPricingTiers();
        setTiers(rows);
      } catch {
        setTiers([]);
      }
    })();
  }, []);

  // Function to check if the current step has valid selections
  const isStepValid = (): boolean => {
    switch (viewStep) {
      case 1: {
        // Require DOB (MM/DD/YYYY), height (ft + in), and weight (lbs)
        // Accept ISO (yyyy-MM-dd). For backward compatibility, also accept MM/DD/YYYY.
        const parsedIso =
          formData.dob && /^\d{4}-\d{2}-\d{2}$/.test(formData.dob)
            ? parseYYYYMMDDToLocalDate(formData.dob)
            : null;
        const parsedUs = formData.dob ? parse(formData.dob, "MM/dd/yyyy", new Date()) : null;
        const dobDate = parsedIso && isValidDate(parsedIso) ? parsedIso : (parsedUs && isValidDate(parsedUs) ? parsedUs : null);
        const dobOk = !!dobDate && (() => {
          const now = new Date();
          const age = now.getFullYear() - dobDate.getFullYear() - ((now.getMonth() < dobDate.getMonth() || (now.getMonth() === dobDate.getMonth() && now.getDate() < dobDate.getDate())) ? 1 : 0);
          return age >= 13 && age <= 100;
        })();
        const feet = Number(formData.heightFeet);
        const inches = Number(formData.heightInches);
        const heightCm = Number(formData.heightCm);
        const weight = Number(formData.bodyWeight);
        const heightOk = formData.heightUnit === "metric"
          ? Number.isFinite(heightCm) && heightCm > 0
          : Number.isFinite(feet) && feet >= 0 && Number.isFinite(inches) && inches >= 0 && inches <= 11 && (feet > 0 || inches > 0);
        const weightOk = Number.isFinite(weight) && weight > 0;
        const genderOk = !!formData.gender;
        return dobOk && heightOk && weightOk && genderOk;
      }
      case 2:
        // Motivation
        return formData.motivation.length > 0;
      case 3:
        // Activity level
        return !!formData.activityLevel;
      case 4:
        // Workout frequency
        return !!formData.workoutFrequency && !!formData.workoutDuration;
      case 5:
        // Exercise types
        if (formData.exerciseTypes.length === 0) return false;
        // If user selects Sports, require details.
        if (formData.exerciseTypes.includes("sports")) {
          return !!formData.sportsEnjoyed.trim();
        }
        return true;
      case 6:
        // Location
        return formData.location.length > 0;
      case 7:
        // Timeline (success metrics/gym persona removed)
        return !!formData.resultTimeframe;
      case 8:
        // Event training
        // If user selected "no" or hasn't selected anything, step is valid
        if (!formData.trainingForEvent || formData.trainingForEvent === "no") {
          return true;
        }
        // If user selected "yes", require both event details and date
        return !!(formData.eventDetails && formData.eventDetails.trim() && formData.eventDate);
      // Subscription plan
      case 9:
        // Subscription plan
        return !!formData.selectedPlan;
      // Must select a plan
      default:
        return false;
    }
  };

  const persistOnboarding = async (overrides: Partial<FormData> = {}) => {
    const data = { ...formData, ...overrides };
    await saveOnboarding({
      gender: data.gender || null,
      dob: data.dob || null,
      heightFeet: data.heightFeet ? Number(data.heightFeet) : null,
      heightInches: data.heightInches ? Number(data.heightInches) : null,
      heightCm: data.heightCm ? Number(data.heightCm) : null,
      heightUnit: data.heightUnit || null,
      bodyWeight: data.bodyWeight ? Number(data.bodyWeight) : null,
      weightUnit: data.weightUnit || null,
      motivation: data.motivation,
      goals: data.goals,
      activityLevel: data.activityLevel || null,
      workoutFrequency: data.workoutFrequency || null,
      workoutDuration: data.workoutDuration || null,
      location: Array.isArray(data.location) ? data.location : (data.location ? [data.location] : []),
      exerciseTypes: data.exerciseTypes,
      sportsEnjoyed: data.sportsEnjoyed?.trim() ? data.sportsEnjoyed.trim() : null,
      healthConditions: data.healthConditions || null,
      injuries: data.injuries || null,
      fitnessLevel: data.fitnessLevel || null,
      successMetrics: data.successMetrics,
      gymPersona: data.gymPersona,
      resultTimeframe: data.resultTimeframe || null,
      specificGoals: data.specificGoals,
      currentMetrics: data.currentMetrics,
      additionalServices: data.additionalServices,
      trainingForEvent: data.trainingForEvent || null,
      eventDetails: data.eventDetails || null,
      eventDate: data.eventDate || null,
      selectedPlan: data.selectedPlan || null,
      billingCycle: data.billingCycle || null,
    });
    await queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.me });
  };

  const beginCheckout = async (tierId: string) => {
    setCheckoutLoadingPlan(tierId);
    try {
      await persistOnboarding({ selectedPlan: tierId });
    } catch (error) {
      console.error("Failed to save onboarding data before checkout:", error);
      setCheckoutLoadingPlan(null);
      toast.error("Failed to save your selections. Please try again.");
      return;
    }

    try {
      const session = await createCheckoutSession({
        pricingTierId: tierId,
        billingCycle: formData.billingCycle,
      });
      toast.success("Redirecting you to secure checkout...");
      
      // Remove the beforeunload listener before redirecting to Stripe
      if (beforeUnloadHandlerRef.current) {
        window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
        beforeUnloadHandlerRef.current = null;
      }
      
      // Redirect to Stripe checkout
      window.location.href = session.checkout_url;
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      setCheckoutLoadingPlan(null);
      toast.error("Unable to start checkout. Please try again.");
    }
  };

  const scrollToTop = () => {
    // Use immediate scroll for better UX during onboarding
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleComplete = async () => {
    if (!formData.selectedPlan) {
      toast.error("Please select a plan to continue.");
      return;
    }
    await beginCheckout(formData.selectedPlan);
  };

  const handleNext = async () => {
    if (!isStepValid()) {
      toast.error("Please make a selection before continuing");
      return;
    }
    
    // Save onboarding data before advancing
    try {
      await persistOnboarding();
    } catch (error) {
      console.error("Failed to save onboarding data:", error);
      toast.error("Failed to save progress. Please try again.");
      return;
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      // Scroll to top immediately after state update
      setTimeout(() => scrollToTop(), 0);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Scroll to top immediately after state update
      setTimeout(() => scrollToTop(), 0);
    }
  };

  const updateFormData = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Function to toggle a single item in array fields
  const toggleArrayItem = (field: keyof FormData, item: string) => {
    setFormData(prev => {
      const currentValue = prev[field];

      // Check if the field is an array before using includes
      if (Array.isArray(currentValue)) {
        // For motivation, limit to 2 selections; for gymPersona and successMetrics, limit to 3 selections
        if (field === 'motivation' && !currentValue.includes(item) && currentValue.length >= 2) {
          return prev; // Don't add if already have 2 selections for motivation
        }
        if ((field === 'gymPersona' || field === 'successMetrics') && !currentValue.includes(item) && currentValue.length >= 3) {
          return prev; // Don't add if already have 3 selections for gymPersona or successMetrics
        }
        const removing = currentValue.includes(item);
        const newValue = removing ? currentValue.filter(i => i !== item) : [...currentValue, item];
        // If user unselects "sports", clear sports details to avoid stale data.
        if (field === "exerciseTypes" && item === "sports" && removing) {
          return {
            ...prev,
            [field]: newValue,
            sportsEnjoyed: "",
          };
        }
        return {
          ...prev,
          [field]: newValue,
        };
      }

      // If somehow we get here with a non-array field, return unchanged state
      return prev;
    });
  };

  const updateGoalValue = (metricId: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      specificGoals: {
        ...prev.specificGoals,
        [metricId]: value
      }
    }));
  };

  const updateCurrentMetric = (metricId: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      currentMetrics: {
        ...prev.currentMetrics,
        [metricId]: value
      }
    }));
  };

  const renderStep = () => {
    switch (viewStep) {
      case 1:
        return <div className="space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <div className="w-14 h-14 sm:w-20 sm:h-20 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon-glow">
                <Heart className="w-10 h-10 text-black" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">
                Welcome, {profile?.first_name}! 👋
              </h2>
              <p className="text-gray-light">Let's customize your fitness journey</p>
            </div>
            {/* Gender and DOB on one row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Gender</Label>
                <Select
                  value={formData.gender || ""}
                  onValueChange={value => updateFormData("gender", value)}
                >
                  <SelectTrigger className="bg-dark-slate border-primary/20 text-white">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-dark-card border-primary/20">
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                    <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white">Date of Birth</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal bg-dark-slate border-primary/20 text-white",
                        !formData.dob && "text-gray-mid"
                      )}
                    >
                      {(() => {
                        const iso =
                          formData.dob && /^\d{4}-\d{2}-\d{2}$/.test(formData.dob)
                            ? parseYYYYMMDDToLocalDate(formData.dob)
                            : null;
                        const us = formData.dob ? parse(formData.dob, "MM/dd/yyyy", new Date()) : null;
                        const date = iso && isValidDate(iso) ? iso : (us && isValidDate(us) ? us : null);
                        return date ? format(date, "MM/dd/yyyy") : "MM/DD/YYYY";
                      })()}
                      <CalendarDays className="ml-auto h-4 w-4 opacity-70" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[min(320px,calc(100vw-1.5rem))] sm:w-[360px]" align="start">
                    <DobPicker
                      value={formData.dob}
                      onChange={(iso) => updateFormData("dob", iso)}
                    />
                  </PopoverContent>
                </Popover>
                {(() => {
                  const parsedIso =
                    formData.dob && /^\d{4}-\d{2}-\d{2}$/.test(formData.dob)
                      ? parseYYYYMMDDToLocalDate(formData.dob)
                      : null;
                  const parsedUs = formData.dob ? parse(formData.dob, "MM/dd/yyyy", new Date()) : null;
                  const dobDate = parsedIso && isValidDate(parsedIso) ? parsedIso : (parsedUs && isValidDate(parsedUs) ? parsedUs : null);
                  if (!formData.dob) return null;
                  if (!dobDate) return <p className="text-xs text-red-400 mt-1">Enter a valid date.</p>;
                  const now = new Date();
                  const age = now.getFullYear() - dobDate.getFullYear() - ((now.getMonth() < dobDate.getMonth() || (now.getMonth() === dobDate.getMonth() && now.getDate() < dobDate.getDate())) ? 1 : 0);
                  if (age < 13) return <p className="text-xs text-red-400 mt-1">You must be at least 13 years old.</p>;
                  if (age > 100) return <p className="text-xs text-red-400 mt-1">Please enter a realistic date of birth.</p>;
                  return null;
                })()}
              </div>
            </div>

            {/* Height */}
            <div className="space-y-2">
              <Label className="text-white">Height</Label>
              {formData.heightUnit === "metric" ? (
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <Input
                      id="height-cm"
                      type="number"
                      min={1}
                      step="1"
                      placeholder="180"
                      value={formData.heightCm || ""}
                      onChange={(e) => updateFormData("heightCm", e.target.value)}
                      className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid pr-14"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-light">cm</span>
                  </div>
                  <ToggleGroup
                    type="single"
                    value={formData.heightUnit}
                    onValueChange={(v) => v && updateFormData("heightUnit", v as "imperial" | "metric")}
                    className="w-[120px] justify-center bg-dark-slate/60 border border-primary/20 rounded-md"
                  >
                    <ToggleGroupItem value="imperial" className="px-3 py-2 text-xs">ft/in</ToggleGroupItem>
                    <ToggleGroupItem value="metric" className="px-3 py-2 text-xs">cm</ToggleGroupItem>
                  </ToggleGroup>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
                  <div className="relative">
                    <Input
                      id="height-feet"
                      type="number"
                      min={0}
                      step="1"
                      placeholder="5"
                      value={formData.heightFeet || ""}
                      onChange={(e) => updateFormData("heightFeet", e.target.value)}
                      className="w-full bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid pr-10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-light">ft</span>
                  </div>
                  <div className="relative">
                    <Input
                      id="height-inches"
                      type="number"
                      min={0}
                      max={11}
                      step="1"
                      placeholder="10"
                      value={formData.heightInches || ""}
                      onChange={(e) => updateFormData("heightInches", e.target.value)}
                      className="w-full bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid pr-10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-light">in</span>
                  </div>
                  <ToggleGroup
                    type="single"
                    value={formData.heightUnit}
                    onValueChange={(v) => v && updateFormData("heightUnit", v as "imperial" | "metric")}
                    className="w-[120px] justify-self-end flex justify-center bg-dark-slate/60 border border-primary/20 rounded-md"
                  >
                    <ToggleGroupItem value="imperial" className="px-3 py-2 text-xs">ft/in</ToggleGroupItem>
                    <ToggleGroupItem value="metric" className="px-3 py-2 text-xs">cm</ToggleGroupItem>
                  </ToggleGroup>
                </div>
              )}
            </div>

            {/* Weight */}
            <div className="space-y-2">
              <Label className="text-white">Weight</Label>
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Input
                    id="body-weight"
                    type="number"
                    min={1}
                    step="1"
                    placeholder={formData.weightUnit === "kg" ? "82" : "180"}
                    value={formData.bodyWeight || ""}
                    onChange={(e) => updateFormData("bodyWeight", e.target.value)}
                    className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid pr-14"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-light">
                    {formData.weightUnit === "kg" ? "kg" : "lbs"}
                  </span>
                </div>
                <ToggleGroup
                  type="single"
                  value={formData.weightUnit}
                  onValueChange={(v) => v && updateFormData("weightUnit", v as "lb" | "kg")}
                  className="w-[120px] justify-center bg-dark-slate/60 border border-primary/20 rounded-md"
                >
                  <ToggleGroupItem value="lb" className="px-3 py-2 text-xs">lbs</ToggleGroupItem>
                  <ToggleGroupItem value="kg" className="px-3 py-2 text-xs">kg</ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>
          </div>;
      case 2:
        return <div className="space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <div className="w-14 h-14 sm:w-20 sm:h-20 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon-glow">
                <Target className="w-10 h-10 text-black" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">Motivations? 🔥</h2>
              <p className="text-gray-light">Select up to 2 motivations that drive you</p>
              {formData.motivation.length > 0 && <div className="mt-2 text-sm font-medium text-primary">
                  {formData.motivation.length}/2 selected
                </div>}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {motivations.map(motivation => <div key={motivation.id}>
                  <Checkbox id={motivation.id} checked={formData.motivation.includes(motivation.id)} onCheckedChange={() => toggleArrayItem("motivation", motivation.id)} className="peer sr-only" disabled={!formData.motivation.includes(motivation.id) && formData.motivation.length >= 2} />
                  <Label htmlFor={motivation.id} className={cn("flex flex-col items-center justify-center p-6 border-2 rounded-2xl cursor-pointer transition-all bg-dark-slate text-white hover:border-primary/50 hover:bg-dark-slate/80 peer-data-[state=checked]:border-primary peer-data-[state=checked]:shadow-neon-glow", !formData.motivation.includes(motivation.id) && formData.motivation.length >= 2 && "opacity-50 cursor-not-allowed hover:bg-dark-slate hover:border-primary/20")}>
                    <span className="text-3xl mb-2">{motivation.icon}</span>
                    <span className="font-medium text-center">{motivation.label}</span>
                  </Label>
                </div>)}
            </div>
            
            {formData.motivation.length > 0 && <div className="p-4 bg-primary/10 rounded-lg border border-primary/30 mt-4">
                <p className="font-medium text-white">Your top motivations:</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.motivation.map(id => {
                const selected = motivations.find(m => m.id === id);
                return <span key={id} className="inline-flex items-center gap-1 bg-dark-card text-white px-3 py-1 rounded-full text-sm border border-primary/20">
                        <span>{selected?.icon}</span> {selected?.label}
                      </span>;
              })}
                </div>
              </div>}
          </div>;
      case 3:
        return <div className="space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <div className="w-14 h-14 sm:w-20 sm:h-20 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon-glow">
                <Activity className="w-10 h-10 text-black" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">Current Activity Level</h2>
              <p className="text-gray-light">Select your current activity level</p>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              {activityLevelOptions.map(option => <div key={option.value} className="transition-all duration-300">
                  <RadioGroup value={formData.activityLevel} onValueChange={value => updateFormData("activityLevel", value)} className="w-full">
                    <RadioGroupItem value={option.value} id={option.value} className="peer sr-only" />
                    <Label htmlFor={option.value} className={cn("flex flex-col p-3 sm:p-5 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white", formData.activityLevel === option.value ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50")}>
                      <div className="text-center">
                        <div className="font-bold text-lg mb-2">{option.label}</div>
                        <div className="text-gray-light/70 text-sm">{option.description}</div>
                      </div>
                    </Label>
                  </RadioGroup>
                </div>)}
            </div>
            
            {formData.activityLevel && <div className="mt-8 animate-fade-in">
                <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                  <div className="font-bold mb-1 text-white">Great choice!</div>
                  <div className="text-sm text-gray-light">We'll tailor your program based on your current activity level.</div>
                </div>
              </div>}
          </div>;
      case 4:
        return <div className="space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <div className="w-14 h-14 sm:w-20 sm:h-20 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon-glow">
                <CalendarIcon className="w-10 h-10 text-black" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">Workout Frequency</h2>
              <p className="text-gray-light">How often do you want to train each week?</p>
            </div>
            
            <div className="grid grid-cols-1 gap-5">
              {workoutFrequencyOptions.map(option => <div key={option.value} className="transition-all duration-300 transform hover:scale-105">
                  <RadioGroup value={formData.workoutFrequency} onValueChange={value => updateFormData("workoutFrequency", value)} className="w-full">
                    <RadioGroupItem value={option.value} id={option.value} className="peer sr-only" />
                    <Label htmlFor={option.value} className={cn("flex flex-col p-3 sm:p-5 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white", formData.workoutFrequency === option.value ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50")}>
                      <div className="flex flex-col items-center gap-1 mb-2">
                        <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                          <CalendarIcon className="w-6 h-6 text-white" />
                        </div>
                      </div>

                      <div className="text-center">
                        <div className="font-bold text-lg mb-1">{option.label}</div>
                        <div className="text-gray-light/70 text-sm mb-2">{option.desc}</div>
                      </div>
                      
                      <div className="mt-3 bg-primary/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-center mx-auto text-white">{option.trophy.name}</div>
                    </Label>
                  </RadioGroup>
                </div>)}
            </div>
            
            {formData.workoutFrequency && <div className="mt-8 animate-fade-in">
                <div className="space-y-4">
                  <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                    <div className="font-bold mb-1 text-white">Great choice!</div>
                    <div className="text-sm text-gray-light">We'll tailor your weekly plan for {workoutFrequencyOptions.find(o => o.value === formData.workoutFrequency)?.label?.toLowerCase()}.</div>
                  </div>
                  
                  <div className="space-y-3">
                    <p className="text-white font-medium">How long do you typically train per session?</p>
                    <RadioGroup value={formData.workoutDuration} onValueChange={value => updateFormData("workoutDuration", value)} className="flex flex-wrap gap-2 sm:grid sm:grid-cols-3 sm:gap-3">
                      {workoutDurationOptions.map(duration => (
                        <div key={duration.value}>
                          <RadioGroupItem value={duration.value} id={duration.value} className="peer sr-only" />
                          <Label htmlFor={duration.value} className={cn(
                            "flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white",
                            formData.workoutDuration === duration.value ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50"
                          )}>
                            <span className="font-medium">{duration.label}</span>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
              </div>}
          </div>;
      case 5:
        return <div className="space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <div className="w-14 h-14 sm:w-20 sm:h-20 bg-strength rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Dumbbell className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">What types of exercise do you enjoy? 💪</h2>
              <p className="text-gray-light">Choose all that interest you or you'd like to try</p>
            </div>
            
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-1 sm:gap-4">
              {exerciseTypeOptions.map(exercise => <div key={exercise.id}>
                  <Checkbox id={`exercise-${exercise.id}`} checked={formData.exerciseTypes.includes(exercise.id)} onCheckedChange={() => toggleArrayItem("exerciseTypes", exercise.id)} className="peer sr-only" />
                  <Label htmlFor={`exercise-${exercise.id}`} className={cn("flex flex-col items-center justify-center p-3 sm:p-6 border-2 rounded-2xl cursor-pointer transition-all bg-dark-slate text-white hover:border-primary/50 hover:bg-dark-slate/80", formData.exerciseTypes.includes(exercise.id) ? "border-primary shadow-neon-glow" : "border-primary/20")}>
                    <div className={cn("rounded-full p-2 sm:p-3 mb-2 sm:mb-3 text-2xl sm:text-4xl", formData.exerciseTypes.includes(exercise.id) ? "bg-primary/20" : "bg-primary/10")}>
                      {exercise.icon}
                    </div>
                    <span className="font-medium text-center text-sm sm:text-lg">{exercise.label}</span>
                    <span className="text-xs sm:text-sm text-center text-gray-light mt-1 line-clamp-2 sm:line-clamp-none hidden sm:block">
                      {exercise.description}
                    </span>
                  </Label>
                </div>)}
            </div>

            {formData.exerciseTypes.includes("sports") && (
              <div className="mt-6 space-y-2 bg-primary/10 p-4 rounded-lg border border-primary/30 animate-fade-in">
                <Label htmlFor="sports-enjoyed" className="text-white">
                  Which sports do you enjoy?
                </Label>
                <Textarea
                  id="sports-enjoyed"
                  placeholder="e.g., Soccer, tennis, basketball"
                  value={formData.sportsEnjoyed}
                  onChange={(e) => updateFormData("sportsEnjoyed", e.target.value)}
                  className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid min-h-[90px]"
                />
                <p className="text-xs text-gray-light">
                  Add the sports you like—comma-separated is totally fine.
                </p>
              </div>
            )}

            {formData.exerciseTypes.length > 0 && <div className="mt-6 bg-primary/10 p-4 rounded-lg border border-primary/30 animate-fade-in">
                <div className="font-medium text-lg text-white">
                  Great choices! {formData.exerciseTypes.length === 1 ? "You've selected 1 activity." : `You've selected ${formData.exerciseTypes.length} activities.`}
                </div>
                <p className="text-gray-light mt-1">
                  We'll customize your workout plan based on your preferences.
                </p>
              </div>}
          </div>;
      case 6:
        return <div className="space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <div className="w-14 h-14 sm:w-20 sm:h-20 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon-glow">
                <Activity className="w-10 h-10 text-black" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">Workout Location 🏋️</h2>
              <p className="text-gray-light">Where do you usually train? Select all that apply</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {["At Home", "Commercial Gym", "Outdoor / Park"].map((locationOption) => {
                const checked = formData.location.includes(locationOption);
                return (
                  <div key={locationOption}>
                    <Checkbox
                      id={`location-${locationOption}`}
                      checked={checked}
                      onCheckedChange={() => toggleArrayItem("location", locationOption)}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={`location-${locationOption}`}
                      className={cn(
                        "flex flex-col items-center justify-center p-6 border-2 rounded-2xl cursor-pointer transition-all bg-dark-slate text-white hover:border-primary/50 hover:bg-dark-slate/80",
                        checked ? "border-primary shadow-neon-glow" : "border-primary/20"
                      )}
                    >
                      <span className="text-3xl mb-2">
                        {locationOption === "At Home" ? "🏠" : locationOption === "Commercial Gym" ? "🏋️" : "🌳"}
                      </span>
                      <span className="font-medium text-center">{locationOption}</span>
                    </Label>
                  </div>
                );
              })}
            </div>
            
            {formData.location.length > 0 && (
              <div className="p-4 bg-primary/10 rounded-lg border border-primary/30 mt-4">
                <p className="font-medium text-white">Your workout locations:</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.location.map((loc) => (
                    <span
                      key={loc}
                      className="inline-flex items-center gap-1 bg-dark-card text-white px-3 py-1 rounded-full text-sm border border-primary/20"
                    >
                      {loc}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>;
      // Removed Gym Persona step
      case 7:
        return <div className="space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <div className="w-14 h-14 sm:w-20 sm:h-20 bg-mobility rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CalendarIcon className="w-10 h-10 text-black" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">Set Your Timeline</h2>
              <p className="text-gray-light">Define your timeframe and specific goals for success metrics</p>
            </div>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-white">Choose your desired timeframe</h3>
                <RadioGroup value={formData.resultTimeframe} onValueChange={value => updateFormData("resultTimeframe", value)} className="grid grid-cols-1 gap-3">
                  {timeframeOptions.map(option => <div key={option.value} className="transition-all duration-300">
                      <RadioGroupItem value={option.value} id={`timeframe-${option.value}`} className="peer sr-only" />
                      <Label htmlFor={`timeframe-${option.value}`} className={cn("flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white", formData.resultTimeframe === option.value ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50")}>
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-sm text-gray-light">{option.description}</div>
                        </div>
                        <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", formData.resultTimeframe === option.value ? "bg-primary text-black" : "bg-dark-card border border-primary/20")}>
                          {formData.resultTimeframe === option.value ? "✓" : ""}
                        </div>
                      </Label>
                    </div>)}
                </RadioGroup>
              </div>

              {formData.successMetrics.length > 0 && <div>
                  <h3 className="text-xl font-semibold mb-4 text-white">Set specific goals for your metrics</h3>
                  <div className="space-y-4 p-5 bg-primary/10 rounded-xl border border-primary/30">
                    <p className="text-sm text-primary mb-4">
                      Define your specific goals based on the success metrics you've selected
                    </p>
                    
                    <div className="grid grid-cols-1 gap-4">
                      {formData.successMetrics.includes('body-weight') && <div className="space-y-4 p-4 bg-dark-card rounded-lg border border-primary/20">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">⚖️</span>
                            <Label className="text-lg font-semibold text-white">Body Weight</Label>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="current-weight" className="text-sm text-gray-light">Current Weight</Label>
                              <Input id="current-weight" type="text" placeholder="e.g. 180 lbs or 82 kg" value={formData.currentMetrics?.['body-weight'] || ''} onChange={e => updateCurrentMetric('body-weight', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="goal-weight" className="text-sm text-gray-light">Target Weight</Label>
                              <Input id="goal-weight" type="text" placeholder="e.g. 150 lbs or 68 kg" value={formData.specificGoals?.['body-weight'] || ''} onChange={e => updateGoalValue('body-weight', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                          </div>
                        </div>}
                      
                      {formData.successMetrics.includes('body-fat') && <div className="space-y-4 p-4 bg-dark-card rounded-lg border border-primary/20">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">📉</span>
                            <Label className="text-lg font-semibold text-white">Body Fat %</Label>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="current-bodyfat" className="text-sm text-gray-light">Current Body Fat %</Label>
                              <Input id="current-bodyfat" type="text" placeholder="e.g. 25%" value={formData.currentMetrics?.['body-fat'] || ''} onChange={e => updateCurrentMetric('body-fat', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="goal-bodyfat" className="text-sm text-gray-light">Target Body Fat %</Label>
                              <Input id="goal-bodyfat" type="text" placeholder="e.g. 15%" value={formData.specificGoals?.['body-fat'] || ''} onChange={e => updateGoalValue('body-fat', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                          </div>
                        </div>}
                      
                      {formData.successMetrics.includes('jeans-size') && <div className="space-y-4 p-4 bg-dark-card rounded-lg border border-primary/20">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">👖</span>
                            <Label className="text-lg font-semibold text-white">Jeans Size</Label>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="current-jeans" className="text-sm text-gray-light">Current Jeans Size</Label>
                              <Input id="current-jeans" type="text" placeholder="e.g. Size 12 or 34" value={formData.currentMetrics?.['jeans-size'] || ''} onChange={e => updateCurrentMetric('jeans-size', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="goal-jeans" className="text-sm text-gray-light">Target Jeans Size</Label>
                              <Input id="goal-jeans" type="text" placeholder="e.g. Size 8 or 32" value={formData.specificGoals?.['jeans-size'] || ''} onChange={e => updateGoalValue('jeans-size', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                          </div>
                        </div>}

                      {formData.successMetrics.includes('tshirt-size') && <div className="space-y-4 p-4 bg-dark-card rounded-lg border border-primary/20">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">👕</span>
                            <Label className="text-lg font-semibold text-white">T-Shirt Size</Label>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="current-tshirt" className="text-sm text-gray-light">Current T-Shirt Size</Label>
                              <Input id="current-tshirt" type="text" placeholder="e.g. Large or XL" value={formData.currentMetrics?.['tshirt-size'] || ''} onChange={e => updateCurrentMetric('tshirt-size', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="goal-tshirt" className="text-sm text-gray-light">Target T-Shirt Size</Label>
                              <Input id="goal-tshirt" type="text" placeholder="e.g. Medium or Large" value={formData.specificGoals?.['tshirt-size'] || ''} onChange={e => updateGoalValue('tshirt-size', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                          </div>
                        </div>}

                      {formData.successMetrics.includes('energy-level') && <div className="space-y-4 p-4 bg-dark-card rounded-lg border border-primary/20">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">⚡</span>
                            <Label className="text-lg font-semibold text-white">Energy Level</Label>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="current-energy" className="text-sm text-gray-light">Current Energy Level</Label>
                              <Select value={formData.currentMetrics?.['energy-level'] || ''} onValueChange={value => updateCurrentMetric('energy-level', value)}>
                                <SelectTrigger className="bg-dark-slate border-primary/20 text-white">
                                  <SelectValue placeholder="Select current level" />
                                </SelectTrigger>
                                <SelectContent className="bg-dark-card border-primary/20">
                                  <SelectItem value="low">Low energy most of the day</SelectItem>
                                  <SelectItem value="moderate">Moderate energy</SelectItem>
                                  <SelectItem value="high">High energy</SelectItem>
                                  <SelectItem value="inconsistent">Inconsistent energy</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="goal-energy" className="text-sm text-gray-light">Target Energy Level</Label>
                              <Select value={formData.specificGoals?.['energy-level'] || ''} onValueChange={value => updateGoalValue('energy-level', value)}>
                                <SelectTrigger className="bg-dark-slate border-primary/20 text-white">
                                  <SelectValue placeholder="Select your goal" />
                                </SelectTrigger>
                                <SelectContent className="bg-dark-card border-primary/20">
                                  <SelectItem value="high">High throughout the day</SelectItem>
                                  <SelectItem value="morning">Better morning energy</SelectItem>
                                  <SelectItem value="afternoon">Better afternoon energy</SelectItem>
                                  <SelectItem value="evening">Better evening energy</SelectItem>
                                  <SelectItem value="consistent">Consistent energy all day</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>}

                      {formData.successMetrics.includes('strength') && <div className="space-y-4 p-4 bg-dark-card rounded-lg border border-primary/20">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">💪</span>
                            <Label className="text-lg font-semibold text-white">Strength</Label>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="current-strength" className="text-sm text-gray-light">Current Strength</Label>
                              <Input id="current-strength" type="text" placeholder="e.g. Bench press 150 lbs" value={formData.currentMetrics?.['strength'] || ''} onChange={e => updateCurrentMetric('strength', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="goal-strength" className="text-sm text-gray-light">Target Strength</Label>
                              <Input id="goal-strength" type="text" placeholder="e.g. Bench press 200 lbs" value={formData.specificGoals?.['strength'] || ''} onChange={e => updateGoalValue('strength', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                          </div>
                        </div>}

                      {formData.successMetrics.includes('bmi') && <div className="space-y-4 p-4 bg-dark-card rounded-lg border border-primary/20">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">📊</span>
                            <Label className="text-lg font-semibold text-white">BMI</Label>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="current-bmi" className="text-sm text-gray-light">Current BMI</Label>
                              <Input id="current-bmi" type="text" placeholder="e.g. 28" value={formData.currentMetrics?.['bmi'] || ''} onChange={e => updateCurrentMetric('bmi', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="goal-bmi" className="text-sm text-gray-light">Target BMI</Label>
                              <Input id="goal-bmi" type="text" placeholder="e.g. 22" value={formData.specificGoals?.['bmi'] || ''} onChange={e => updateGoalValue('bmi', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                          </div>
                        </div>}

                      {formData.successMetrics.includes('visceral-fat') && <div className="space-y-4 p-4 bg-dark-card rounded-lg border border-primary/20">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">🔍</span>
                            <Label className="text-lg font-semibold text-white">Visceral Fat Level</Label>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="current-visceral" className="text-sm text-gray-light">Current Visceral Fat Level</Label>
                              <Input id="current-visceral" type="text" placeholder="e.g. Level 12" value={formData.currentMetrics?.['visceral-fat'] || ''} onChange={e => updateCurrentMetric('visceral-fat', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="goal-visceral" className="text-sm text-gray-light">Target Visceral Fat Level</Label>
                              <Input id="goal-visceral" type="text" placeholder="e.g. Level 7" value={formData.specificGoals?.['visceral-fat'] || ''} onChange={e => updateGoalValue('visceral-fat', e.target.value)} className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                            </div>
                          </div>
                        </div>}
                    </div>
                  </div>
                </div>}

              {formData.resultTimeframe && Object.keys(formData.specificGoals).length > 0 && <div className="p-5 bg-primary/10 rounded-xl border border-primary/30 animate-fade-in">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">🎯</div>
                    <div>
                      <h4 className="font-bold text-lg mb-1 text-white">Your personalized plan is ready!</h4>
                      <p className="text-gray-light">
                        {formData.resultTimeframe === '1-month' && 'We\'ll create an intensive 1-month program to help you reach your goals quickly.'}
                        {formData.resultTimeframe === '3-months' && 'We\'ll create a balanced 3-month program to help you achieve sustainable results.'}
                        {formData.resultTimeframe === '6-months' && 'We\'ll create a progressive 6-month program for steady, lasting transformation.'}
                        {formData.resultTimeframe === '12-months' && 'We\'ll create a comprehensive 12-month program for complete lifestyle transformation.'}
                      </p>
                      <p className="text-sm text-primary mt-3">
                        Your plan will focus on the {Object.keys(formData.specificGoals).length} specific goals you've set.
                      </p>
                    </div>
                  </div>
                </div>}
            </div>
          </div>;
      case 8:
        return <div className="space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <div className="w-14 h-14 sm:w-20 sm:h-20 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon-glow">
                <Trophy className="w-10 h-10 text-black" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">Training for an Event? 🏆</h2>
              <p className="text-gray-light">Are you training for a specific event or competition?</p>
            </div>
            
            <div className="space-y-6">
              <RadioGroup 
                value={formData.trainingForEvent || ""} 
                onValueChange={value => {
                  updateFormData("trainingForEvent", value);
                  // Clear event details and date if user selects "No"
                  if (value === "no") {
                    updateFormData("eventDetails", "");
                    updateFormData("eventDate", "");
                  }
                }}
                className="grid grid-cols-2 gap-4"
              >
                <div>
                  <RadioGroupItem value="yes" id="event-yes" className="peer sr-only" />
                  <Label 
                    htmlFor="event-yes" 
                    className={cn(
                      "flex flex-col items-center justify-center p-6 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white",
                      formData.trainingForEvent === "yes" ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50"
                    )}
                  >
                    <span className="text-3xl mb-2">✅</span>
                    <span className="font-medium text-lg">Yes</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="no" id="event-no" className="peer sr-only" />
                  <Label 
                    htmlFor="event-no" 
                    className={cn(
                      "flex flex-col items-center justify-center p-6 border-2 rounded-xl cursor-pointer transition-all bg-dark-slate text-white",
                      formData.trainingForEvent === "no" ? "border-primary shadow-neon-glow" : "border-primary/20 hover:border-primary/50"
                    )}
                  >
                    <span className="text-3xl mb-2">❌</span>
                    <span className="font-medium text-lg">No</span>
                  </Label>
                </div>
              </RadioGroup>

              {formData.trainingForEvent === "yes" && (
                <div className="space-y-6 mt-8 animate-fade-in">
                  <div className="space-y-2">
                    <Label htmlFor="event-details" className="text-white">Tell us about your event</Label>
                    <Textarea
                      id="event-details"
                      placeholder="e.g., Marathon in Boston, Powerlifting competition, Triathlon, etc."
                      value={formData.eventDetails || ""}
                      onChange={(e) => updateFormData("eventDetails", e.target.value)}
                      className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid min-h-[100px]"
                    />
                    <p className="text-xs text-gray-light">Describe the event you're training for</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Event Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal bg-dark-slate border-primary/20 text-white",
                            !formData.eventDate && "text-gray-mid"
                          )}
                        >
                          {formData.eventDate ? (
                            format(parseYYYYMMDDToLocalDate(formData.eventDate), "MMMM d, yyyy")
                          ) : (
                            "Select event date"
                          )}
                          <CalendarDays className="ml-auto h-4 w-4 opacity-70" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-auto" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.eventDate ? parseYYYYMMDDToLocalDate(formData.eventDate) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              updateFormData("eventDate", format(date, "yyyy-MM-dd"));
                            }
                          }}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date < today;
                          }}
                          className="bg-dark-card border-primary/20"
                        />
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-gray-light">When is your event?</p>
                  </div>

                  {formData.eventDetails && formData.eventDate && (
                    <div className="p-4 bg-primary/10 rounded-lg border border-primary/30 mt-4">
                      <p className="font-medium text-white mb-2">Event Summary:</p>
                      <p className="text-gray-light text-sm mb-1">
                        <span className="font-medium">Event:</span> {formData.eventDetails}
                      </p>
                      <p className="text-gray-light text-sm">
                        <span className="font-medium">Date:</span> {format(parseYYYYMMDDToLocalDate(formData.eventDate), "MMMM d, yyyy")}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {formData.trainingForEvent === "no" && (
                <div className="mt-8 animate-fade-in">
                  <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                    <p className="text-gray-light">
                      No problem! We'll create a fitness plan tailored to your goals.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>;
      case 9:
        return <div className="space-y-6">
            <div className="text-center mb-4 sm:mb-8">
              <div className="w-14 h-14 sm:w-20 sm:h-20 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon-glow">
                <SparklesIcon className="w-10 h-10 text-black" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white">Choose Your Coaching Plan 🚀</h2>
              <p className="text-gray-light">Select the perfect plan to achieve your fitness goals</p>
            </div>

            <div className="mb-6">
              <div className="flex justify-center mb-6">
                <div className="bg-dark-slate p-1 rounded-full inline-flex">
                  <button onClick={() => updateFormData("billingCycle", "monthly")} className={cn("px-4 py-2 rounded-full text-sm font-medium transition-all", formData.billingCycle === "monthly" ? "bg-primary text-black shadow-neon-glow" : "text-gray-light hover:text-white")}>
                    Monthly
                  </button>
                  <button onClick={() => updateFormData("billingCycle", "annual")} className={cn("px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2", formData.billingCycle === "annual" ? "bg-primary text-black shadow-neon-glow" : "text-gray-light hover:text-white")}>
                    <span>Annual</span>
                    <Badge variant="default" className="bg-strength text-white text-xs">
                      Save 40%
                    </Badge>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {tiers.map((t) => (
                  <div key={t.id} className={cn(
                    "relative rounded-2xl border bg-dark-card/80 backdrop-blur-sm p-6 flex flex-col",
                    t.highlight ? "border-primary/50 shadow-neon-glow-strong" : "border-primary/20 shadow-neon-glow",
                    formData.selectedPlan === t.id && "ring-2 ring-primary/60"
                  )}>
                    {t.highlight && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-black text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">Most Popular</div>
                    )}
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold text-white">{t.name}</h3>
                      {formData.selectedPlan === t.id && (
                        <div className="h-6 w-6 bg-primary rounded-full" />
                      )}
                    </div>
                    <p className="text-gray-light mt-1">{t.blurb}</p>
                    <div className="mt-4">
                      <span className="text-3xl font-black text-white">
                        ${formData.billingCycle === "monthly" ? t.price_monthly : t.price_annual}
                      </span>
                      <span className="text-gray-light ml-2">/ {formData.billingCycle}</span>
                    </div>
                    <ul className="mt-4 space-y-2 text-gray-light">
                      {t.features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <span className="mt-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-black text-[10px]">✓</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6">
                      <Button
                        className={cn(
                          "w-full font-bold uppercase tracking-wider",
                          formData.selectedPlan === t.id ? "shadow-neon-glow" : "shadow-neon-glow hover:scale-105 transition-all",
                          checkoutLoadingPlan === t.id && "opacity-80 cursor-wait"
                        )}
                        variant={formData.selectedPlan === t.id ? "default" : "outline"}
                        disabled={!!checkoutLoadingPlan}
                        onClick={async () => {
                          updateFormData("selectedPlan", t.id);
                          await beginCheckout(t.id);
                        }}
                      >
                        {checkoutLoadingPlan === t.id
                          ? "Redirecting..."
                          : formData.selectedPlan === t.id
                            ? "Selected"
                            : "Choose Plan"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {formData.selectedPlan && <div className="p-5 bg-primary/10 rounded-xl border border-primary/30 animate-fade-in">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/20 p-3 rounded-full">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1 text-white">
                      Selected: {tiers.find(t => t.id === formData.selectedPlan)?.name} {formData.billingCycle === "annual" ? "(Annual)" : "(Monthly)"}
                    </h4>
                    <p className="text-gray-light mb-1">
                      {tiers.find(t => t.id === formData.selectedPlan)?.blurb}
                    </p>
                    <p className="text-sm text-primary font-medium">
                      {formData.billingCycle === "annual" ? "Billed annually" : "Billed monthly"}
                    </p>
                  </div>
                </div>
              </div>}

            <div className="mt-6 bg-mobility/20 p-4 rounded-lg border border-mobility/30">
              <div className="flex items-start gap-3">
                <div className="text-mobility mt-1">
                  <CalendarCheck className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-medium text-white">Money Back Guarantee</h4>
                  <p className="text-sm text-gray-light">
                    Try risk-free for 14 days. If you're not satisfied, we'll refund your payment.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-light mt-8 pt-6 border-t border-primary/20">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>Secure payment</span>
              </div>
              <a href="/pricing" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-lime-hover">Learn more about our plans</a>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen gradient-dark p-3 sm:p-4 overflow-x-hidden">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={signOut}
            className="absolute right-0 top-0 text-gray-light hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
          <div className="flex items-center justify-center mb-4">
            <img
              src="https://xnjrgbjrqhbqsfocowbt.supabase.co/storage/v1/object/public/Logos/image4.jpeg"
              alt="Frequent Health logo"
              className="h-12 w-auto"
            />
          </div>
        </div>

        {/* Sticky top progress with step markers (moved below header) */}
        <div className="sticky top-0 z-[70] -mx-4 px-4 pt-2 pb-3 bg-dark-card/60 supports-[backdrop-filter]:bg-dark-card/40 backdrop-blur border-b border-primary/20 mb-6 overflow-x-hidden">
          <div className="max-w-2xl mx-auto">
            <div className="mb-2 flex items-center justify-between text-[11px] sm:text-xs text-gray-light">
              <span className="tracking-wide">
                {isMobile ? (
                  <>Step {currentStep} of {totalSteps}: {steps[activeIndex]}</>
                ) : (
                  "Onboarding"
                )}
              </span>
              <span className="tabular-nums">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-1.5" />
            <div className="mt-3 hidden sm:grid grid-cols-9 gap-1.5 sm:gap-2 min-w-0">
              {steps.map((label, idx) => {
                const completed = idx < activeIndex;
                const current = idx === activeIndex;
                return (
                  <div key={label} className="flex flex-col items-center min-w-0">
                    <div
                      className={cn(
                        "relative h-5 w-5 sm:h-6 sm:w-6 rounded-full border transition-all duration-300",
                        completed && "border-primary bg-gradient-to-br from-primary to-lime-hover",
                        current && "border-primary bg-dark-slate ring-2 ring-primary/50 scale-105",
                        !completed && !current && "border-primary/30 bg-dark-slate"
                      )}
                    >
                      {/* Connector lines */}
                      {idx > 0 && (
                        <div
                          className={cn(
                            "absolute left-[-12px] top-1/2 -translate-y-1/2 h-[2px] w-[12px] sm:w-[16px]",
                            (completed || current) ? "bg-gradient-to-r from-primary to-lime-hover" : "bg-primary/20"
                          )}
                        />
                      )}
                      {idx < steps.length - 1 && (
                        <div
                          className={cn(
                            "absolute right-[-12px] top-1/2 -translate-y-1/2 h-[2px] w-[12px] sm:w-[16px]",
                            (idx < activeIndex - 0) ? "bg-gradient-to-r from-primary to-lime-hover" : "bg-primary/20"
                          )}
                        />
                      )}
                      <div className={cn(
                        "absolute inset-0 flex items-center justify-center text-[10px] sm:text-[11px] text-white/80",
                        completed && "text-white"
                      )}>
                        {completed ? "✓" : idx + 1}
                      </div>
                    </div>
                    <div className="sr-only">{label}</div>
                    <div className="hidden sm:block mt-1 text-[11px] text-gray-light max-w-[84px] text-center truncate min-w-0">
                      {label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Form Card */}
        <Card className="border-primary/20 shadow-neon-glow bg-dark-card">
          <CardContent className={cn(
            "p-4 sm:p-6 md:p-8",
            isMobile && currentStep !== totalSteps && "pb-client-nav"
          )}>
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation (hidden on final Plan step; selection buttons proceed) */}
        {currentStep !== totalSteps && (
          <div className={cn(
            "flex justify-between mt-8", 
            isMobile && "fixed bottom-0 left-0 right-0 bg-dark-card border-t border-primary/20 p-4 pb-safe-bottom shadow-neon-glow z-[60]"
          )}>
            <Button 
              variant="outline" 
              onClick={handleBack} 
              disabled={currentStep === 1} 
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </Button>
            
            <Button 
              onClick={handleNext} 
              disabled={!isStepValid()} 
              className={cn(
                "flex items-center space-x-2 font-bold uppercase tracking-wider", 
                isStepValid() 
                  ? "shadow-neon-glow hover:scale-105 transition-all" 
                  : "opacity-50 cursor-not-allowed"
              )}
            >
              <span>{currentStep === totalSteps ? "Complete Setup" : "Continue"}</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>;
};
export default ClientOnboarding;
